import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ApplicationAuthorizations,
  ApplicationStatus,
  AprilAdiExecutionsPage,
  AprilAdiIntegrationResult,
  ArianeDuplicateInspection,
  BouquetServiceConsultantsPage,
  ConsultantBouquetServiceSubscriptionAction,
  ConsultantViewerCompliance,
  ConsultantViewerContacts,
  ConsultantViewerOrganization,
  ConsultantViewerOverview,
  ConsultantViewerSearchPage,
  CorrectionExecution,
  CorrectionExecutionRequest,
  CorrectionPreview,
  DuplicatedArianeInvestorsPage,
  ExPostCampaignDetail,
  ExPostCampaignSummary,
  ExPostDocumentsPage,
  ExPostEmailBatch,
  ExPostImportSummary,
  InvestorCrossConsultantRelationsPage,
  InvestorArianeDump,
  InvestorBoxPlacement,
  InvestorImmoOptionIntention,
  InvestorGedDocuments,
  InvestorAtlasInspection,
  InvestorPersonCorrection,
  InvestorPersonCorrectionRequest,
  InvestorPersonCorrectionSuggestion,
  InvestorRelationDeletion,
  InvestorInspection,
  InvestorMinotaureInspection,
  InvestorPurgeCheck,
  InvestorPurgeExecution,
  InvestorViewerCommand,
  InvestorViewerConsultantAssignment,
  InvestorViewerFinancialData,
  InvestorViewerOverview,
  InvestorViewerPerson,
  InvestorViewerSearchPage,
  MetadataCorrectionWorkspace,
  MinotaureRecueilCrossConsultantPage,
  MultiplePersonnesPhysiquesInvestorsPage,
  ProblematicMetadataReport,
  ProxeemaInvestorSignature,
  ProxeemaSignatureStateCategory,
  ProxeemaSignatureStateOption,
  ProxeemaSignatureDocument,
  ProxeemaUniversignTransaction,
  CancelProxeemaUniversignTransactionRequest,
  CancelProxeemaGedSignatureRequest,
  ProxeemaGedSignatureCancellation,
  ProxeemaUniversignMatch,
  ConfirmProxeemaUniversignSignatureRequest,
  ProxeemaUniversignConfirmation,
  ProxeemaSignatureAnomaly,
  ProxeemaSignatureAnomalyReport,
  ProxeemaUniversignIndexStatus,
  TechnicalCachesStatus,
  TechnicalInvestorPurgeExecutionsPage,
  UpdateProxeemaSignatureStateRequest
} from '../models/api.models';

@Injectable({
  providedIn: 'root'
})
export class DoublonsBusterApiService {
  private readonly apiBaseUrl = environment.apiBaseUrl.replace(/\/$/, '');

  constructor(private readonly httpClient: HttpClient) {}

  getApplicationStatus(): Observable<ApplicationStatus> {
    return this.httpClient.get<ApplicationStatus>(this.buildUrl('/application/status'));
  }

  getApplicationAuthorizations(): Observable<ApplicationAuthorizations> {
    return this.httpClient.get<ApplicationAuthorizations>(this.buildUrl('/application/authorizations'));
  }

  getProxeemaSignatureStateOptions(
    category: ProxeemaSignatureStateCategory
  ): Observable<ProxeemaSignatureStateOption[]> {
    const params = new HttpParams().set('category', category);
    return this.httpClient.get<ProxeemaSignatureStateOption[]>(
      this.buildUrl('/proxeema/signatures/state-options'),
      { params }
    );
  }

  getProxeemaInvestorSignature(investorId: number): Observable<ProxeemaInvestorSignature> {
    return this.httpClient.get<ProxeemaInvestorSignature>(
      this.buildUrl(`/proxeema/signatures/investisseurs/${investorId}`)
    );
  }

  getProxeemaInvestorSignatureDocuments(investorId: number): Observable<ProxeemaSignatureDocument[]> {
    return this.httpClient.get<ProxeemaSignatureDocument[]>(
      this.buildUrl(`/proxeema/signatures/investisseurs/${investorId}/documents`)
    );
  }

  updateProxeemaSignatureState(
    investorId: number,
    category: ProxeemaSignatureStateCategory,
    request: UpdateProxeemaSignatureStateRequest
  ): Observable<ProxeemaInvestorSignature> {
    return this.httpClient.put<ProxeemaInvestorSignature>(
      this.buildUrl(`/proxeema/signatures/investisseurs/${investorId}/states/${category}`),
      request
    );
  }

  getProxeemaPendingUniversignTransactions(
    since: string,
    until: string
  ): Observable<ProxeemaUniversignTransaction[]> {
    const params = new HttpParams().set('since', since).set('until', until);
    return this.httpClient.get<ProxeemaUniversignTransaction[]>(
      this.buildUrl('/proxeema/signatures/universign/pending-transactions'),
      { params }
    );
  }

  cancelProxeemaUniversignTransaction(
    transactionId: string,
    request: CancelProxeemaUniversignTransactionRequest
  ): Observable<void> {
    return this.httpClient.post<void>(
      this.buildUrl(`/proxeema/signatures/universign/transactions/${encodeURIComponent(transactionId)}/cancel`),
      request
    );
  }

  cancelProxeemaGedSignature(
    investorId: number,
    documentId: string,
    request: CancelProxeemaGedSignatureRequest
  ): Observable<ProxeemaGedSignatureCancellation> {
    return this.httpClient.post<ProxeemaGedSignatureCancellation>(
      this.buildUrl(`/proxeema/signatures/investisseurs/${investorId}/documents/${documentId}/cancel`),
      request
    );
  }

  /**
   * Sans bornes, le serveur restreint la recherche aux environs de l'envoi en signature du document.
   */
  getProxeemaUniversignMatches(
    investorId: number,
    documentId: string,
    since?: string,
    until?: string
  ): Observable<ProxeemaUniversignMatch[]> {
    let params = new HttpParams();
    if (since && until) {
      params = params.set('since', since).set('until', until);
    }

    return this.httpClient.get<ProxeemaUniversignMatch[]>(
      this.buildUrl(`/proxeema/signatures/investisseurs/${investorId}/documents/${documentId}/universign-matches`),
      { params }
    );
  }

  getProxeemaUniversignIndexStatus(since: string, until: string): Observable<ProxeemaUniversignIndexStatus> {
    const params = new HttpParams().set('since', since).set('until', until);
    return this.httpClient.get<ProxeemaUniversignIndexStatus>(
      this.buildUrl('/proxeema/signatures/anomalies/index-universign'),
      { params }
    );
  }

  requestProxeemaUniversignIndexing(since: string, until: string): Observable<unknown> {
    const params = new HttpParams().set('since', since).set('until', until);
    return this.httpClient.post(
      this.buildUrl('/proxeema/signatures/anomalies/index-universign'),
      null,
      { params }
    );
  }

  getProxeemaExtraDocuments(since: string, until: string): Observable<ProxeemaSignatureAnomaly[]> {
    const params = new HttpParams().set('since', since).set('until', until);
    return this.httpClient.get<ProxeemaSignatureAnomaly[]>(
      this.buildUrl('/proxeema/signatures/anomalies/documents-supplementaires'),
      { params }
    );
  }

  confirmProxeemaUniversignSignature(
    investorId: number,
    documentId: string,
    request: ConfirmProxeemaUniversignSignatureRequest
  ): Observable<ProxeemaUniversignConfirmation> {
    return this.httpClient.post<ProxeemaUniversignConfirmation>(
      this.buildUrl(`/proxeema/signatures/investisseurs/${investorId}/documents/${documentId}/confirm-universign-signature`),
      request
    );
  }

  confirmProxeemaUniversignDocument(
    investorId: number,
    documentId: string,
    request: ConfirmProxeemaUniversignSignatureRequest
  ): Observable<ProxeemaUniversignConfirmation> {
    return this.httpClient.post<ProxeemaUniversignConfirmation>(
      this.buildUrl(`/proxeema/signatures/investisseurs/${investorId}/documents/${documentId}/confirm-universign-document`),
      request
    );
  }

  downloadProxeemaUniversignDocument(
    investorId: number,
    documentId: string,
    transactionId: string
  ): Observable<Blob> {
    return this.httpClient.get(
      this.buildUrl(
        `/proxeema/signatures/investisseurs/${investorId}/documents/${documentId}/universign-transactions/${transactionId}/download`
      ),
      { responseType: 'blob' }
    );
  }

  getProxeemaSignatureAnomalies(since: string, until: string): Observable<ProxeemaSignatureAnomalyReport> {
    const params = new HttpParams().set('since', since).set('until', until);
    return this.httpClient.get<ProxeemaSignatureAnomalyReport>(
      this.buildUrl('/proxeema/signatures/anomalies'),
      { params }
    );
  }

  exportProxeemaSignatureAnomalies(since: string, until: string): Observable<Blob> {
    const params = new HttpParams().set('since', since).set('until', until);
    return this.httpClient.get(this.buildUrl('/proxeema/signatures/anomalies/export'), {
      params,
      responseType: 'blob'
    });
  }

  getTechnicalCachesStatus(): Observable<TechnicalCachesStatus> {
    return this.httpClient.get<TechnicalCachesStatus>(this.buildUrl('/application/technical/caches'));
  }

  getTechnicalInvestorPurgeExecutionsPage(
    pageIndex: number,
    pageSize: number
  ): Observable<TechnicalInvestorPurgeExecutionsPage> {
    const params = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize);

    return this.httpClient.get<TechnicalInvestorPurgeExecutionsPage>(
      this.buildUrl('/application/technical/investor-purge-executions'),
      { params }
    );
  }

  getProblematicMetadataReport(): Observable<ProblematicMetadataReport> {
    return this.httpClient.get<ProblematicMetadataReport>(this.buildUrl('/problematic-metadata'));
  }

  getDuplicatedArianeInvestorsPage(
    pageIndex: number,
    pageSize: number,
    filters: {
      arianeId?: number | null;
      investorId?: number | null;
      investorCode?: string;
      investorName?: string;
      investorStatuses?: string[];
      consultantId?: number | null;
      consultantCode?: string;
      consultantName?: string;
      excludeContacts?: boolean;
      differentConsultantsOnly?: boolean;
    }
  ): Observable<DuplicatedArianeInvestorsPage> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize);

    if (filters.arianeId !== null && filters.arianeId !== undefined) {
      params = params.set('arianeId', filters.arianeId);
    }

    if (filters.investorId !== null && filters.investorId !== undefined) {
      params = params.set('investorId', filters.investorId);
    }

    if (filters.investorCode?.trim()) {
      params = params.set('investorCode', filters.investorCode.trim());
    }

    if (filters.investorName?.trim()) {
      params = params.set('investorName', filters.investorName.trim());
    }

    (filters.investorStatuses ?? [])
      .filter((status: string) => status.trim())
      .forEach((status: string) => {
        params = params.append('investorStatuses', status.trim());
      });

    if (filters.consultantId !== null && filters.consultantId !== undefined) {
      params = params.set('consultantId', filters.consultantId);
    }

    if (filters.consultantCode?.trim()) {
      params = params.set('consultantCode', filters.consultantCode.trim());
    }

    if (filters.consultantName?.trim()) {
      params = params.set('consultantName', filters.consultantName.trim());
    }

    if (filters.excludeContacts) {
      params = params.set('excludeContacts', filters.excludeContacts);
    }

    if (filters.differentConsultantsOnly) {
      params = params.set('differentConsultantsOnly', filters.differentConsultantsOnly);
    }

    return this.httpClient.get<DuplicatedArianeInvestorsPage>(this.buildUrl('/atlas/doublons-ariane'), {
      params
    });
  }

  exportDuplicatedArianeInvestors(
    filters: {
      arianeId?: number | null;
      investorId?: number | null;
      investorCode?: string;
      investorName?: string;
      investorStatuses?: string[];
      consultantId?: number | null;
      consultantCode?: string;
      consultantName?: string;
      excludeContacts?: boolean;
      differentConsultantsOnly?: boolean;
    }
  ): Observable<Blob> {
    let params = new HttpParams();

    if (filters.arianeId !== null && filters.arianeId !== undefined) {
      params = params.set('arianeId', filters.arianeId);
    }

    if (filters.investorId !== null && filters.investorId !== undefined) {
      params = params.set('investorId', filters.investorId);
    }

    if (filters.investorCode?.trim()) {
      params = params.set('investorCode', filters.investorCode.trim());
    }

    if (filters.investorName?.trim()) {
      params = params.set('investorName', filters.investorName.trim());
    }

    (filters.investorStatuses ?? [])
      .filter(status => status.trim())
      .forEach(status => {
        params = params.append('investorStatuses', status.trim());
      });

    if (filters.consultantId !== null && filters.consultantId !== undefined) {
      params = params.set('consultantId', filters.consultantId);
    }

    if (filters.consultantCode?.trim()) {
      params = params.set('consultantCode', filters.consultantCode.trim());
    }

    if (filters.consultantName?.trim()) {
      params = params.set('consultantName', filters.consultantName.trim());
    }

    if (filters.excludeContacts) {
      params = params.set('excludeContacts', filters.excludeContacts);
    }

    if (filters.differentConsultantsOnly) {
      params = params.set('differentConsultantsOnly', filters.differentConsultantsOnly);
    }

    return this.httpClient.get(this.buildUrl('/atlas/doublons-ariane/export'), {
      params,
      responseType: 'blob'
    });
  }

  getInvestorsWithMultiplePersonnesPhysiquesPage(
    pageIndex: number,
    pageSize: number,
    filters: {
      personnePhysiqueId?: number | null;
      investorId?: number | null;
      investorCode?: string;
      investorName?: string;
      investorStatuses?: string[];
      consultantId?: number | null;
      consultantCode?: string;
      consultantName?: string;
    }
  ): Observable<MultiplePersonnesPhysiquesInvestorsPage> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize);

    if (filters.personnePhysiqueId !== null && filters.personnePhysiqueId !== undefined) {
      params = params.set('personnePhysiqueId', filters.personnePhysiqueId);
    }

    if (filters.investorId !== null && filters.investorId !== undefined) {
      params = params.set('investorId', filters.investorId);
    }

    if (filters.investorCode?.trim()) {
      params = params.set('investorCode', filters.investorCode.trim());
    }

    if (filters.investorName?.trim()) {
      params = params.set('investorName', filters.investorName.trim());
    }

    (filters.investorStatuses ?? [])
      .filter(status => status.trim())
      .forEach(status => {
        params = params.append('investorStatuses', status.trim());
      });

    if (filters.consultantId !== null && filters.consultantId !== undefined) {
      params = params.set('consultantId', filters.consultantId);
    }

    if (filters.consultantCode?.trim()) {
      params = params.set('consultantCode', filters.consultantCode.trim());
    }

    if (filters.consultantName?.trim()) {
      params = params.set('consultantName', filters.consultantName.trim());
    }

    return this.httpClient.get<MultiplePersonnesPhysiquesInvestorsPage>(
      this.buildUrl('/atlas/doublons-personne-physique'),
      {
        params
      }
    );
  }

  exportInvestorsWithMultiplePersonnesPhysiques(
    filters: {
      personnePhysiqueId?: number | null;
      investorId?: number | null;
      investorCode?: string;
      investorName?: string;
      investorStatuses?: string[];
      consultantId?: number | null;
      consultantCode?: string;
      consultantName?: string;
    }
  ): Observable<Blob> {
    let params = new HttpParams();

    if (filters.personnePhysiqueId !== null && filters.personnePhysiqueId !== undefined) {
      params = params.set('personnePhysiqueId', filters.personnePhysiqueId);
    }

    if (filters.investorId !== null && filters.investorId !== undefined) {
      params = params.set('investorId', filters.investorId);
    }

    if (filters.investorCode?.trim()) {
      params = params.set('investorCode', filters.investorCode.trim());
    }

    if (filters.investorName?.trim()) {
      params = params.set('investorName', filters.investorName.trim());
    }

    (filters.investorStatuses ?? [])
      .filter(status => status.trim())
      .forEach(status => {
        params = params.append('investorStatuses', status.trim());
      });

    if (filters.consultantId !== null && filters.consultantId !== undefined) {
      params = params.set('consultantId', filters.consultantId);
    }

    if (filters.consultantCode?.trim()) {
      params = params.set('consultantCode', filters.consultantCode.trim());
    }

    if (filters.consultantName?.trim()) {
      params = params.set('consultantName', filters.consultantName.trim());
    }

    return this.httpClient.get(this.buildUrl('/atlas/doublons-personne-physique/export'), {
      params,
      responseType: 'blob'
    });
  }

  getInvestorsWithCrossConsultantRelationsPage(
    pageIndex: number,
    pageSize: number,
    filters: {
      investorId?: number | null;
      consultantId?: number | null;
      consultantCode?: string;
      consultantName?: string;
    }
  ): Observable<InvestorCrossConsultantRelationsPage> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize);

    if (filters.investorId !== null && filters.investorId !== undefined) {
      params = params.set('investorId', filters.investorId);
    }

    if (filters.consultantId !== null && filters.consultantId !== undefined) {
      params = params.set('consultantId', filters.consultantId);
    }

    if (filters.consultantCode?.trim()) {
      params = params.set('consultantCode', filters.consultantCode.trim());
    }

    if (filters.consultantName?.trim()) {
      params = params.set('consultantName', filters.consultantName.trim());
    }

    return this.httpClient.get<InvestorCrossConsultantRelationsPage>(
      this.buildUrl('/atlas/relations-inter-consultants'),
      {
        params
      }
    );
  }

  exportInvestorsWithCrossConsultantRelations(
    filters: {
      investorId?: number | null;
      consultantId?: number | null;
      consultantCode?: string;
      consultantName?: string;
    }
  ): Observable<Blob> {
    let params = new HttpParams();

    if (filters.investorId !== null && filters.investorId !== undefined) {
      params = params.set('investorId', filters.investorId);
    }

    if (filters.consultantId !== null && filters.consultantId !== undefined) {
      params = params.set('consultantId', filters.consultantId);
    }

    if (filters.consultantCode?.trim()) {
      params = params.set('consultantCode', filters.consultantCode.trim());
    }

    if (filters.consultantName?.trim()) {
      params = params.set('consultantName', filters.consultantName.trim());
    }

    return this.httpClient.get(this.buildUrl('/atlas/relations-inter-consultants/export'), {
      params,
      responseType: 'blob'
    });
  }

  getBouquetServiceConsultantsPage(
    pageIndex: number,
    pageSize: number,
    filters: {
      consultantId?: number | null;
      consultantCode?: string;
      consultantName?: string;
      subscriptionStatuses?: string[];
    }
  ): Observable<BouquetServiceConsultantsPage> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize);

    if (filters.consultantId !== null && filters.consultantId !== undefined) {
      params = params.set('consultantId', filters.consultantId);
    }

    if (filters.consultantCode?.trim()) {
      params = params.set('consultantCode', filters.consultantCode.trim());
    }

    if (filters.consultantName?.trim()) {
      params = params.set('consultantName', filters.consultantName.trim());
    }

    (filters.subscriptionStatuses ?? []).forEach(status => {
      params = params.append('subscriptionStatuses', status);
    });

    return this.httpClient.get<BouquetServiceConsultantsPage>(
      this.buildUrl('/atlas/bouquet-service'),
      { params }
    );
  }

  activateConsultantBouquetServiceSubscription(
    consultantId: number,
    endDate: string
  ): Observable<ConsultantBouquetServiceSubscriptionAction> {
    return this.httpClient.post<ConsultantBouquetServiceSubscriptionAction>(
      this.buildUrl(`/atlas/consultants/${consultantId}/bouquet-service/activation`),
      { endDate }
    );
  }

  terminateConsultantBouquetServiceSubscription(
    consultantId: number,
    terminationDate: string
  ): Observable<ConsultantBouquetServiceSubscriptionAction> {
    return this.httpClient.post<ConsultantBouquetServiceSubscriptionAction>(
      this.buildUrl(`/atlas/consultants/${consultantId}/bouquet-service/resiliation`),
      { terminationDate }
    );
  }

  getMinotaureRecueilCrossConsultantPage(
    pageIndex: number,
    pageSize: number,
    filters: {
      metadataId?: number | null;
      recueilKind?: string | null;
      recueilObjectId?: number | null;
      investorId?: number | null;
      consultantId?: number | null;
      consultantCode?: string;
      consultantName?: string;
    }
  ): Observable<MinotaureRecueilCrossConsultantPage> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize);

    params = this.appendMinotaureRecueilCrossConsultantFilters(params, filters);

    return this.httpClient.get<MinotaureRecueilCrossConsultantPage>(
      this.buildUrl('/minotaure/recueil-inter-consultants'),
      { params }
    );
  }

  exportMinotaureRecueilCrossConsultant(
    filters: {
      metadataId?: number | null;
      recueilKind?: string | null;
      recueilObjectId?: number | null;
      investorId?: number | null;
      consultantId?: number | null;
      consultantCode?: string;
      consultantName?: string;
    }
  ): Observable<Blob> {
    let params = new HttpParams();
    params = this.appendMinotaureRecueilCrossConsultantFilters(params, filters);

    return this.httpClient.get(this.buildUrl('/minotaure/recueil-inter-consultants/export'), {
      params,
      responseType: 'blob'
    });
  }

  getInvestorInspection(investorId: number): Observable<InvestorInspection> {
    return this.httpClient.get<InvestorInspection>(this.buildUrl(`/atlas/investisseurs/${investorId}`));
  }

  getInvestorAtlasInspection(investorId: number): Observable<InvestorAtlasInspection> {
    return this.httpClient.get<InvestorAtlasInspection>(this.buildUrl(`/atlas/investisseurs/${investorId}/atlas`));
  }

  getInvestorAtlasViewerOverview(investorId: number): Observable<InvestorViewerOverview> {
    return this.httpClient.get<InvestorViewerOverview>(
      this.buildUrl(`/atlas/investisseurs/${investorId}/viewer/overview`)
    );
  }

  getInvestorAtlasViewerPersons(investorId: number): Observable<InvestorViewerPerson[]> {
    return this.httpClient.get<InvestorViewerPerson[]>(
      this.buildUrl(`/atlas/investisseurs/${investorId}/viewer/persons`)
    );
  }

  getInvestorAtlasViewerPerson(investorId: number, personId: number): Observable<InvestorViewerPerson> {
    return this.httpClient.get<InvestorViewerPerson>(
      this.buildUrl(`/atlas/investisseurs/${investorId}/viewer/persons/${personId}`)
    );
  }

  getInvestorAtlasViewerConsultants(investorId: number): Observable<InvestorViewerConsultantAssignment[]> {
    return this.httpClient.get<InvestorViewerConsultantAssignment[]>(
      this.buildUrl(`/atlas/investisseurs/${investorId}/viewer/consultants`)
    );
  }

  getInvestorAtlasViewerFinancialData(investorId: number): Observable<InvestorViewerFinancialData> {
    return this.httpClient.get<InvestorViewerFinancialData>(
      this.buildUrl(`/atlas/investisseurs/${investorId}/viewer/financial`)
    );
  }

  getInvestorAtlasViewerCommands(investorId: number): Observable<InvestorViewerCommand[]> {
    return this.httpClient.get<InvestorViewerCommand[]>(
      this.buildUrl(`/atlas/investisseurs/${investorId}/viewer/commands`)
    );
  }

  getConsultantAtlasViewerOverview(consultantId: number): Observable<ConsultantViewerOverview> {
    return this.httpClient.get<ConsultantViewerOverview>(
      this.buildUrl(`/atlas/consultants/${consultantId}/viewer/overview`)
    );
  }

  getConsultantAtlasViewerContacts(consultantId: number): Observable<ConsultantViewerContacts> {
    return this.httpClient.get<ConsultantViewerContacts>(
      this.buildUrl(`/atlas/consultants/${consultantId}/viewer/contacts`)
    );
  }

  getConsultantAtlasViewerOrganization(consultantId: number): Observable<ConsultantViewerOrganization> {
    return this.httpClient.get<ConsultantViewerOrganization>(
      this.buildUrl(`/atlas/consultants/${consultantId}/viewer/organization`)
    );
  }

  getConsultantAtlasViewerCompliance(consultantId: number): Observable<ConsultantViewerCompliance> {
    return this.httpClient.get<ConsultantViewerCompliance>(
      this.buildUrl(`/atlas/consultants/${consultantId}/viewer/compliance`)
    );
  }

  getInvestorMinotaureInspection(investorId: number): Observable<InvestorMinotaureInspection> {
    return this.httpClient.get<InvestorMinotaureInspection>(this.buildUrl(`/atlas/investisseurs/${investorId}/minotaure`));
  }

  searchAtlasInvestors(
    pageIndex: number,
    pageSize: number,
    filters: {
      investorId?: number | null;
      investorCode?: string;
      investorName?: string;
      arianeId?: number | null;
      investorStatuses?: string[];
      consultantId?: number | null;
      consultantCode?: string;
      consultantName?: string;
    }
  ): Observable<InvestorViewerSearchPage> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize);

    if (filters.investorId !== null && filters.investorId !== undefined) {
      params = params.set('investorId', filters.investorId);
    }

    if (filters.investorCode?.trim()) {
      params = params.set('investorCode', filters.investorCode.trim());
    }

    if (filters.investorName?.trim()) {
      params = params.set('investorName', filters.investorName.trim());
    }

    if (filters.arianeId !== null && filters.arianeId !== undefined) {
      params = params.set('arianeId', filters.arianeId);
    }

    (filters.investorStatuses ?? [])
      .filter(status => status.trim())
      .forEach(status => {
        params = params.append('investorStatuses', status.trim());
      });

    if (filters.consultantId !== null && filters.consultantId !== undefined) {
      params = params.set('consultantId', filters.consultantId);
    }

    if (filters.consultantCode?.trim()) {
      params = params.set('consultantCode', filters.consultantCode.trim());
    }

    if (filters.consultantName?.trim()) {
      params = params.set('consultantName', filters.consultantName.trim());
    }

    return this.httpClient.get<InvestorViewerSearchPage>(this.buildUrl('/atlas/viewer/investisseurs'), {
      params
    });
  }

  searchAtlasConsultants(
    pageIndex: number,
    pageSize: number,
    filters: {
      consultantId?: number | null;
      consultantCode?: string;
      consultantName?: string;
      arianeId?: number | null;
      consultantStatuses?: string[];
    }
  ): Observable<ConsultantViewerSearchPage> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize);

    if (filters.consultantId !== null && filters.consultantId !== undefined) {
      params = params.set('consultantId', filters.consultantId);
    }

    if (filters.consultantCode?.trim()) {
      params = params.set('consultantCode', filters.consultantCode.trim());
    }

    if (filters.consultantName?.trim()) {
      params = params.set('consultantName', filters.consultantName.trim());
    }

    if (filters.arianeId !== null && filters.arianeId !== undefined) {
      params = params.set('arianeId', filters.arianeId);
    }

    (filters.consultantStatuses ?? [])
      .filter(status => status.trim())
      .forEach(status => {
        params = params.append('consultantStatuses', status.trim());
      });

    return this.httpClient.get<ConsultantViewerSearchPage>(this.buildUrl('/atlas/viewer/consultants'), {
      params
    });
  }

  getArianeDuplicateInspection(arianeId: number): Observable<ArianeDuplicateInspection> {
    return this.httpClient.get<ArianeDuplicateInspection>(this.buildUrl(`/atlas/doublons-ariane/${arianeId}`));
  }

  exportProblematicMetadataReport(): Observable<Blob> {
    return this.httpClient.get(this.buildUrl('/problematic-metadata/export'), {
      responseType: 'blob'
    });
  }

  getMetadataWorkspace(metadataId: number, excludedInvestorIds: number[]): Observable<MetadataCorrectionWorkspace> {
    let params = new HttpParams();

    excludedInvestorIds.forEach(investorId => {
      params = params.append('excludedInvestorIds', investorId);
    });

    return this.httpClient.get<MetadataCorrectionWorkspace>(this.buildUrl(`/problematic-metadata/${metadataId}`), {
      params
    });
  }

  previewCorrection(metadataId: number, investorIdToDelete: number, virtualDeletedInvestorIds: number[]): Observable<CorrectionPreview> {
    return this.httpClient.post<CorrectionPreview>(this.buildUrl(`/problematic-metadata/${metadataId}/preview`), {
      investorIdToDelete,
      virtualDeletedInvestorIds
    });
  }

  executeCorrection(metadataId: number, request: CorrectionExecutionRequest): Observable<CorrectionExecution> {
    return this.httpClient.post<CorrectionExecution>(this.buildUrl(`/problematic-metadata/${metadataId}/execute`), request);
  }

  executeInvestorPersonCorrection(
    investorId: number,
    request: InvestorPersonCorrectionRequest
  ): Observable<InvestorPersonCorrection> {
    return this.httpClient.post<InvestorPersonCorrection>(
      this.buildUrl(`/atlas/investisseurs/${investorId}/personnes-physiques/correction`),
      request
    );
  }

  getInvestorPersonCorrectionSuggestion(investorId: number): Observable<InvestorPersonCorrectionSuggestion> {
    return this.httpClient.get<InvestorPersonCorrectionSuggestion>(
      this.buildUrl(`/atlas/investisseurs/${investorId}/personnes-physiques/correction/suggestion`)
    );
  }

  getInvestorGedDocuments(investorId: number): Observable<InvestorGedDocuments> {
    return this.httpClient.get<InvestorGedDocuments>(
      this.buildUrl(`/atlas/investisseurs/${investorId}/referentiels-externes/ged/documents`)
    );
  }

  getInvestorBoxPlacement(investorId: number): Observable<InvestorBoxPlacement> {
    return this.httpClient.get<InvestorBoxPlacement>(
      this.buildUrl(`/atlas/investisseurs/${investorId}/referentiels-externes/box-placement`)
    );
  }

  getInvestorArianeDump(investorId: number): Observable<InvestorArianeDump> {
    return this.httpClient.get<InvestorArianeDump>(
      this.buildUrl(`/atlas/investisseurs/${investorId}/referentiels-externes/minotaure/arianeDump`)
    );
  }

  getInvestorImmoOptionIntention(investorId: number): Observable<InvestorImmoOptionIntention> {
    return this.httpClient.get<InvestorImmoOptionIntention>(
      this.buildUrl(`/atlas/investisseurs/${investorId}/referentiels-externes/immo-factory`)
    );
  }

  checkInvestorPurge(investorId: number): Observable<InvestorPurgeCheck> {
    return this.httpClient.get<InvestorPurgeCheck>(
      this.buildUrl(`/atlas/investisseurs/${investorId}/purge/check`)
    );
  }

  getActiveInvestorPurgeExecution(investorId: number): Observable<InvestorPurgeExecution> {
    return this.httpClient.get<InvestorPurgeExecution>(
      this.buildUrl(`/atlas/investisseurs/${investorId}/purge/execution`)
    );
  }

  executeInvestorPurge(investorId: number): Observable<InvestorPurgeExecution> {
    return this.httpClient.post<InvestorPurgeExecution>(
      this.buildUrl(`/atlas/investisseurs/${investorId}/purge/executions`),
      {}
    );
  }

  deleteInvestorRelation(
    investorId: number,
    personId: number,
    relationKind: 'famille' | 'autre',
    relationId: number
  ): Observable<InvestorRelationDeletion> {
    return this.httpClient.delete<InvestorRelationDeletion>(
      this.buildUrl(
        `/atlas/investisseurs/${investorId}/viewer/persons/${personId}/relations/${relationKind}/${relationId}`
      )
    );
  }

  deleteInvestorCoupleRelation(
    investorId: number,
    personId: number,
    relatedInvestorId: number,
    relatedPersonId: number
  ): Observable<InvestorRelationDeletion> {
    return this.httpClient.delete<InvestorRelationDeletion>(
      this.buildUrl(
        `/atlas/investisseurs/${investorId}/viewer/persons/${personId}/relations/couple/${relatedInvestorId}/personnes/${relatedPersonId}`
      )
    );
  }

  validateExPostImport(file: File, year: number): Observable<ExPostImportSummary> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('year', `${year}`);

    return this.httpClient.post<ExPostImportSummary>(
      this.buildUrl('/expost/campaigns/validations'),
      formData
    );
  }

  importExPostCampaign(file: File, year: number): Observable<ExPostImportSummary> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('year', `${year}`);

    return this.httpClient.post<ExPostImportSummary>(this.buildUrl('/expost/campaigns'), formData);
  }

  getExPostCampaigns(): Observable<ExPostCampaignSummary[]> {
    return this.httpClient.get<ExPostCampaignSummary[]>(this.buildUrl('/expost/campaigns'));
  }

  getExPostCampaignDetail(campaignId: number): Observable<ExPostCampaignDetail> {
    return this.httpClient.get<ExPostCampaignDetail>(
      this.buildUrl(`/expost/campaigns/${campaignId}`)
    );
  }

  getExPostDocumentsPage(
    campaignId: number,
    pageIndex: number,
    pageSize: number,
    filters: { deliveryMode?: string | null; errorsOnly?: boolean } = {}
  ): Observable<ExPostDocumentsPage> {
    let params = new HttpParams().set('pageIndex', pageIndex).set('pageSize', pageSize);

    if (filters.deliveryMode?.trim()) {
      params = params.set('deliveryMode', filters.deliveryMode.trim());
    }

    if (filters.errorsOnly) {
      params = params.set('errorsOnly', filters.errorsOnly);
    }

    return this.httpClient.get<ExPostDocumentsPage>(
      this.buildUrl(`/expost/campaigns/${campaignId}/documents`),
      { params }
    );
  }

  startExPostGeneration(campaignId: number): Observable<ExPostCampaignSummary> {
    return this.httpClient.post<ExPostCampaignSummary>(
      this.buildUrl(`/expost/campaigns/${campaignId}/generation`),
      {}
    );
  }

  sendNextExPostEmailBatch(campaignId: number): Observable<ExPostEmailBatch> {
    return this.httpClient.post<ExPostEmailBatch>(
      this.buildUrl(`/expost/campaigns/${campaignId}/email-batches/next/send`),
      {}
    );
  }

  resendExPostEmailBatch(campaignId: number, batchId: number): Observable<ExPostEmailBatch> {
    return this.httpClient.post<ExPostEmailBatch>(
      this.buildUrl(`/expost/campaigns/${campaignId}/email-batches/${batchId}/resend`),
      {}
    );
  }

  deleteExPostCampaign(campaignId: number): Observable<void> {
    return this.httpClient.delete<void>(this.buildUrl(`/expost/campaigns/${campaignId}`));
  }

  downloadExPostArchive(campaignId: number, deliveryMode: 'Email' | 'Courrier'): Observable<Blob> {
    const params = new HttpParams().set('deliveryMode', deliveryMode);

    return this.httpClient.get(this.buildUrl(`/expost/campaigns/${campaignId}/downloads`), {
      params,
      responseType: 'blob'
    });
  }

  printExPostArchive(campaignId: number, deliveryMode: 'Email' | 'Courrier'): Observable<Blob> {
    const params = new HttpParams().set('deliveryMode', deliveryMode);

    return this.httpClient.get(this.buildUrl(`/expost/campaigns/${campaignId}/print`), {
      params,
      responseType: 'blob'
    });
  }

  downloadExPostDocumentPdf(documentId: number): Observable<Blob> {
    return this.httpClient.get(this.buildUrl(`/expost/documents/${documentId}/pdf`), {
      responseType: 'blob'
    });
  }

  private appendMinotaureRecueilCrossConsultantFilters(
    params: HttpParams,
    filters: {
      metadataId?: number | null;
      recueilKind?: string | null;
      recueilObjectId?: number | null;
      investorId?: number | null;
      consultantId?: number | null;
      consultantCode?: string;
      consultantName?: string;
    }
  ): HttpParams {
    if (filters.metadataId !== null && filters.metadataId !== undefined) {
      params = params.set('metadataId', filters.metadataId);
    }

    if (filters.recueilKind?.trim()) {
      params = params.set('recueilKind', filters.recueilKind.trim());
    }

    if (filters.recueilObjectId !== null && filters.recueilObjectId !== undefined) {
      params = params.set('recueilObjectId', filters.recueilObjectId);
    }

    if (filters.investorId !== null && filters.investorId !== undefined) {
      params = params.set('investorId', filters.investorId);
    }

    if (filters.consultantId !== null && filters.consultantId !== undefined) {
      params = params.set('consultantId', filters.consultantId);
    }

    if (filters.consultantCode?.trim()) {
      params = params.set('consultantCode', filters.consultantCode.trim());
    }

    if (filters.consultantName?.trim()) {
      params = params.set('consultantName', filters.consultantName.trim());
    }

    return params;
  }

  simulateAprilAdiIntegration(file: File): Observable<AprilAdiIntegrationResult> {
    return this.httpClient.post<AprilAdiIntegrationResult>(
      this.buildUrl('/aramis/april-adi/simulations'),
      this.buildAprilAdiFormData(file)
    );
  }

  executeAprilAdiIntegration(file: File): Observable<AprilAdiIntegrationResult> {
    return this.httpClient.post<AprilAdiIntegrationResult>(
      this.buildUrl('/aramis/april-adi/executions'),
      this.buildAprilAdiFormData(file)
    );
  }

  getAprilAdiExecutionsPage(pageIndex: number, pageSize: number): Observable<AprilAdiExecutionsPage> {
    const params = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize);

    return this.httpClient.get<AprilAdiExecutionsPage>(this.buildUrl('/aramis/april-adi/executions'), {
      params
    });
  }

  downloadAprilAdiRejectionReport(executionId: number): Observable<Blob> {
    return this.httpClient.get(
      this.buildUrl(`/aramis/april-adi/executions/${executionId}/rejection-report`),
      { responseType: 'blob' }
    );
  }

  downloadAprilAdiIntegrationReport(executionId: number): Observable<Blob> {
    return this.httpClient.get(
      this.buildUrl(`/aramis/april-adi/executions/${executionId}/integration-report`),
      { responseType: 'blob' }
    );
  }

  private buildAprilAdiFormData(file: File): FormData {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return formData;
  }

  private buildUrl(path: string): string {
    return `${this.apiBaseUrl}${path}`;
  }
}
